/**
 * Validation Helpers
 *
 * Pure stateless validation functions. No store access — all dependencies
 * are passed as parameters. Safe to import anywhere including outside Vue components.
 *
 * Migrated from: src/mixins/global.js
 */

/**
 * Validates an email address.
 * - Disallows consecutive dots (..)
 * - Ensures the first character is a letter or number
 * - Ensures domain starts with a letter or number
 * - Allows hyphens in the domain but not at start or end
 * - Ensures a valid TLD (e.g. .com, .org, .com.sg, .co.in)
 */
export function validateEmail(email: string): boolean {
  const re =
    /^(?!.*\.\.)[a-zA-Z0-9](?:[a-zA-Z0-9._%+-]*[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/
  return re.test(email)
}

/**
 * Validates a URL (http/https only).
 * Accepts domain names and IPv4 addresses, with optional port and path.
 */
export function validateUrl(url: string): boolean {
  const re = new RegExp(
    '^(https?:\\/\\/)' +
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' +
      '((\\d{1,3}\\.){3}\\d{1,3}))' +
      '(\\:\\d+)?' +
      '([/?#][^\\s]*)?$',
    'i'
  )
  return !!re.test(url)
}

/**
 * Validates a phone number.
 *
 * Returns `true` if valid, or an error message string if invalid.
 * Callers are responsible for displaying the error (e.g. via updateToast).
 *
 * Contract: `const result = validatePhoneNumber(n); if (result !== true) { /* show error *\/ return; }`
 */
export function validatePhoneNumber(number: string | undefined): true | string {
  const phoneNumber = number?.trim()

  if (!phoneNumber) {
    return 'Phone number is required'
  }

  if (phoneNumber.charAt(0) === '+') {
    const validFormat = /^\+[0-9-]+$/.test(phoneNumber)
    if (!validFormat) {
      return 'Invalid phone number format. If using country code (+), please use only numbers after the + sign.'
    }
  } else {
    const validFormat = /^[0-9][0-9-]*$/.test(phoneNumber)
    if (!validFormat) {
      return 'Invalid phone number format. Please use only numbers. For international numbers, start with + followed by country code.'
    }
  }

  if (phoneNumber.length > 15) {
    return 'Phone number is too long (maximum 15 characters)'
  }

  const digitsOnly = phoneNumber.replace(/[^0-9]/g, '')
  if (digitsOnly.length < 10) {
    return 'Phone number is too short'
  }

  return true
}
